import { TNullable, TScheduleType, TServiceServerCamel } from '@/types';
import { MEDWAND_SERVICE_NAME } from '@/utils/constants';

/**
 * Checks whether the service is a system one, auto-created by the BE
 * (e.g. MedWand for Presenter appointments), and must stay hidden from
 * the Admin Panel and scheduling flows.
 * @param service - The service to check.
 * @param service.name - The service name used to detect system services.
 * @returns `true` for a system service.
 */
export const isSystemService = ({ name }: Pick<TServiceServerCamel, 'name'>) =>
  name === MEDWAND_SERVICE_NAME;

/**
 * Picks the services that can be offered in scheduling flows.
 * @param services - All institution services.
 * @param scheduleType - The flow the services are offered in. On-demand
 * keeps only the services that allow 'See a provider now'.
 * @returns Enabled services, excluding system ones.
 */
export const getBookableServices = (
  services: TServiceServerCamel[] = [],
  scheduleType?: TScheduleType,
) =>
  services.filter(
    (service) =>
      service.enabled &&
      !isSystemService(service) &&
      (scheduleType !== 'on_demand' || !!service.allowEncounterNow),
  );

/**
 * Checks whether a patient can request an on-demand appointment, i.e. the
 * institution has at least one service that allows 'See a provider now'.
 * @param services - All institution services.
 * @returns `true` when at least one on-demand service is available.
 */
export const hasOnDemandServices = (
  services?: TNullable<TServiceServerCamel[]>,
) => !!getBookableServices(services ?? [], 'on_demand').length;
